import React, { Component } from 'react';
import { motion } from 'framer-motion';
import StarStory from '../components/StarStory';
import AnimatedCounter from '../components/AnimatedCounter';
const ApolloPage = () => {
  return <div className="bg-white min-h-screen" data-id="element-158">
      <div className="container mx-auto px-4 py-16" data-id="element-159">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-12" data-id="element-160">
          <img src="https://logo.clearbit.com/apollo.io" alt="Apollo.io logo" className="w-24 h-24 object-contain" data-id="element-161" />
          <div data-id="element-162">
            <motion.h1 className="text-4xl font-bold text-gray-800 mb-2" initial={{
            opacity: 0.5
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5
          }} data-id="element-163">
              VP of Product at Apollo.io
            </motion.h1>
            <motion.p className="text-xl text-gray-700 mb-4" initial={{
            opacity: 0.5
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-164">
              2023 — Present
            </motion.p>
            <motion.div className="bg-gray-100 p-4 rounded-lg" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} data-id="element-165">
              <p className="text-gray-700" data-id="element-166">
                <span className="font-bold" data-id="element-167">Context:</span> Leading B2B sales
                intelligence platform. $2.5B valuation.
              </p>
            </motion.div>
          </div>
        </div>
        {/* STAR Stories */}
        <StarStory title="PLG Revenue Acceleration" situation="Apollo was primarily sales-led, with limited self-serve growth. The PLG motion was generating just 12% of revenue despite massive user base potential." task="As VP of Product, I needed to transform Apollo into a PLG-first company while maintaining enterprise sales momentum." action="I rebuilt the entire user journey with a freemium-to-paid focus. We introduced usage limits, in-app upsell triggers, and a value-based pricing model. I restructured the product team into cross-functional squads focused on activation, monetization, and retention. We also implemented robust experimentation infrastructure to test pricing and feature gating." result="PLG revenue grew from 12% → 28% of total revenue in 12 months. Self-serve conversion doubled, and overall revenue growth exceeded 100% YoY, contributing to Apollo's $2.5B valuation." imageUrl="https://via.placeholder.com/600x400?text=Apollo+PLG+Dashboard" imageAlt="Apollo PLG revenue dashboard" animationComponent={<div className="bg-white p-6 rounded-lg shadow-md" data-id="element-169">
              <h4 className="text-lg font-bold mb-4 text-center" data-id="element-170">
                PLG Revenue Growth
              </h4>
              <div className="flex justify-between items-center mb-2" data-id="element-171">
                <span className="text-gray-700" data-id="element-172">PLG Revenue Contribution:</span>
                <div className="flex items-center" data-id="element-173">
                  <span className="text-gray-500 mr-2" data-id="element-174">12%</span>
                  <span className="text-gray-700 mx-2" data-id="element-175">→</span>
                  <AnimatedCounter start={12} end={28} suffix="%" data-id="element-176" />
                </div>
              </div>
              <div className="flex justify-between items-center" data-id="element-177">
                <span className="text-gray-700" data-id="element-178">YoY Revenue Growth:</span>
                <div className="flex items-center" data-id="element-179">
                  <span className="text-gray-700 mr-1" data-id="element-180">+</span>
                  <AnimatedCounter start={0} end={100} suffix="%" data-id="element-181" />
                </div>
              </div>
            </div>} data-id="element-168" />
        <StarStory title="Activation Rate Optimization" situation="User activation was stalling at 22% (defined as users who performed 3+ key actions within 14 days). This created a leaky bucket despite strong acquisition." task="I needed to double activation rates without increasing CAC." action="We redesigned the onboarding experience with personalized user journeys based on job roles and use cases. I introduced interactive tutorials, success checklists, and milestone celebrations. We also built an AI-powered data enrichment feature that delivered immediate value in the first session." result="Activation rates climbed from 22% → 46% within 6 months. This translated to 2.1x more activated users without additional marketing spend, dramatically improving unit economics." imageUrl="https://via.placeholder.com/600x400?text=Apollo+Onboarding" imageAlt="Apollo onboarding flow screenshots" animationComponent={<div className="bg-white p-6 rounded-lg shadow-md" data-id="element-183">
              <h4 className="text-lg font-bold mb-4 text-center" data-id="element-184">
                Activation Metrics
              </h4>
              <div className="flex justify-between items-center mb-2" data-id="element-185">
                <span className="text-gray-700" data-id="element-186">14-Day Activation:</span>
                <div className="flex items-center" data-id="element-187">
                  <span className="text-gray-500 mr-2" data-id="element-188">22%</span>
                  <span className="text-gray-700 mx-2" data-id="element-189">→</span>
                  <AnimatedCounter start={22} end={46} suffix="%" data-id="element-190" />
                </div>
              </div>
              <div className="flex justify-between items-center" data-id="element-191">
                <span className="text-gray-700" data-id="element-192">Activated User Growth:</span>
                <AnimatedCounter start={1} end={2.1} suffix="x" decimals={1} data-id="element-193" />
              </div>
            </div>} data-id="element-182" />
        <StarStory title="Enterprise Feature Expansion" situation="Apollo's enterprise tier needed differentiation to justify premium pricing. Feature parity with competitors was hurting deal closure rates." task="My challenge was to develop unique enterprise capabilities that would increase ASP by 50%." action="I prioritized building advanced account-based prospecting capabilities with custom AI models. We integrated with popular CRMs beyond Salesforce, added team collaboration features, and built enterprise-grade compliance and security controls. I also introduced a customer advisory board to gather feedback from key enterprise users." result="Enterprise ASP increased by 72%, exceeding the target. The sales team reported 30% higher win rates against key competitors, and enterprise revenue doubled YoY." imageUrl="https://via.placeholder.com/600x400?text=Apollo+Enterprise" imageAlt="Apollo enterprise dashboard" animationComponent={<div className="bg-white p-6 rounded-lg shadow-md" data-id="element-195">
              <h4 className="text-lg font-bold mb-4 text-center" data-id="element-196">
                Enterprise Performance
              </h4>
              <div className="flex justify-between items-center mb-2" data-id="element-197">
                <span className="text-gray-700" data-id="element-198">ASP Increase:</span>
                <div className="flex items-center" data-id="element-199">
                  <span className="text-gray-700 mr-1" data-id="element-200">+</span>
                  <AnimatedCounter start={0} end={72} suffix="%" data-id="element-201" />
                </div>
              </div>
              <div className="flex justify-between items-center" data-id="element-202">
                <span className="text-gray-700" data-id="element-203">Competitive Win Rate:</span>
                <div className="flex items-center" data-id="element-204">
                  <span className="text-gray-700 mr-1" data-id="element-205">+</span>
                  <AnimatedCounter start={0} end={30} suffix="%" data-id="element-206" />
                </div>
              </div>
            </div>} data-id="element-194" />
        {/* Testimonial */}
        <motion.div className="mt-16 bg-gray-100 p-8 rounded-lg" initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} data-id="element-207">
          <p className="text-xl text-gray-700 italic mb-4" data-id="element-208">
            "Aakash was a transformational VP of Product, helping us reach $2.5B
            valuation by driving &gt;100% YoY PLG growth."
          </p>
          <p className="text-gray-800 font-bold" data-id="element-209">— Shek Viswanathan, CPO</p>
        </motion.div>
      </div>
    </div>;
};
export default ApolloPage;